import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

import MainLayout from "./layout/MainLayout";
import AuthGuard from "./components/AuthGuard";
import { SetupGuard } from "./pages/SetupGuard";
import RequireCompany from "./components/RequireCompany"; // ✅ import it
import RequireCompanyAdmin from "./components/RequireCompanyAdmin";

// --- Page imports (as you had them) ---
import Dashboard from "./pages/Dashboard";
import SaleEntry from "./pages/SaleEntry";
import PurchaseEntry from "./pages/PurchaseEntry";
import CustomerList from "./pages/CustomerList";
import VendorList from "./pages/VendorList";
import ProductList from "./pages/ProductList";
import SalesList from "./pages/SalesList";
import PurchasesList from "./pages/PurchasesList";
import ReceivablesList from "./pages/ReceivablesList";
import PayablesList from "./pages/PayablesList";
import CreditCardPayableDetail from "./pages/CreditCardPayableDetail";
import SaleDetail from "./pages/SaleDetail";
import SaleEdit from "./pages/SaleEdit";
import CustomerEntry from "./pages/CustomerEntry";
import PurchaseDetail from "./pages/PurchaseDetail";
import PurchaseEdit from "./pages/PurchaseEdit";
import VendorEntry from "./pages/VendorEntry";
import ProductEntry from "./pages/ProductEntry";
import ProductDetail from "./pages/ProductDetail";
import AddPaymentMethod from "./pages/AddPaymentMethod";
import AddBankAccount from "./pages/AddBankAccount";
import BankAccountList from "./pages/BankAccountList";
import PaymentMethodList from "./pages/PaymentMethodList";
import Settings from "./pages/Settings";
import EditPaymentMethod from "./pages/EditPaymentMethod";
import EditBankAccount from "./pages/EditBankAccount";
import CompanyProfile from "./pages/CompanyProfile";
import WelcomeWizard from "./pages/WelcomeWizard";
import ExpenseEntry from "./pages/ExpensesEntry";
import ExpensesList from "./pages/ExpensesList";
import ExpenseDetail from "./pages/ExpensesDetail";
import EditExpenseCategories from "./pages/EditExpenseCategories";
import Reports from "./pages/Reports";
import BalanceSheet from "./pages/BalanceSheet";
import IncomeStatement from "./pages/IncomeStatement";
import CashAnalysis from "./pages/CashAnalysis";
import YearEndClosePage from "./pages/YearEndClose";
import ProJournalEntry from "./pages/ProJournalEntry";
import GeneralJournal from "./pages/GeneralJournal";
import CustomerDetail from "./pages/CustomerDetail";
import VendorDetail from "./pages/VendorDetail";
import Books from "./pages/Books";
import TrialBalance from "./pages/TrialBalance";
import GeneralLedger from "./pages/GeneralLedger";
import RecognitionCenter from "./pages/RecognitionCenter";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ChangePassword from "./pages/ChangePassword";
import Profile from "./pages/Profile";
import AdminUserList from "./pages/AdminUserList";
import OwnerFundsEntry from "./pages/OwnerFundsEntry";
import OwnerFundsList from "./pages/OwnerFundsList";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentCancel from "./pages/PaymentCancel";
import PrivacyControls from "./pages/PrivacyControls";
import InvoiceEmailSettings from "./pages/InvoiceEmailSettings";
import VerifyEmail from "./pages/VerifyEmail";
import StaffInvite from "./pages/StaffInvite";
import StaffAcceptInvite from "./pages/StaffAcceptInvite";
import SpendingLayout from "./pages/spending/SpendingLayout";
import SpendingDepositsList from "./pages/spending/SpendingDepositsList";
import SpendingFixedAssetsList from "./pages/spending/SpendingFixedAssetsList";
import SpendingPrepaidsList from "./pages/spending/SpendingPrepaidsList";
import NewPrepaid from "./pages/prepaids/NewPrepaid";
import PrepaidDetailPage from "./pages/prepaids/PrepaidDetail";
import NewDeposit from "./pages/deposits/NewDeposit";
import DepositDetailPage from "./pages/deposits/DepositDetail";
import NewFixedAsset from "./pages/fixed-assets/NewFixedAsset";
import FixedAssetDetail from "./pages/fixed-assets/FixedAssetDetail";
import FixedAssetEdit from "./pages/fixed-assets/FixedAssetEdit";
import ShopifySettings from "./pages/ShopifySettings";
import ShopifyImportedOrders from "./pages/shopify/ShopifyImportedOrders";
import ShopifySyncIssues from "./pages/shopify/ShopifySyncIssues";
import ShopifyWebhookEvents from "./pages/shopify/ShopifyWebhookEvents";
import CommerceCenter from "./pages/commerce/CommerceCenter";
import ShopifyProductMapping from "./pages/commerce/ShopifyProductMapping";
import Scanner from "./pages/Scanner";

function App() {
  return (
    <Router>
      <Routes>
        {/* --- PUBLIC ROUTES (unchanged) --- */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/invite/accept" element={<StaffAcceptInvite />} /> {/* renamed */}

        {/* --- PROTECTED + COMPANY-SCOPED ROUTES --- */}
        <Route
          path="/company/:company_id"
          element={
            <AuthGuard>
              <SetupGuard>
                <MainLayout />
              </SetupGuard>
            </AuthGuard>
          }
        >
          {/* ✅ Everything under here is company-membership checked */}
          <Route element={<RequireCompany />}>
            {/* --- COMMON ROUTES (admins + staff_POS) --- */}
            <Route index element={<Dashboard />} />
            <Route path="welcome" element={<WelcomeWizard />} />
            <Route path="dashboard" element={<Dashboard />} />

            {/* POS / front-of-house routes staff_POS can use */}
            <Route path="sales" element={<SalesList />} />
            <Route path="sales/new" element={<SaleEntry />} />
            <Route path="sales/:id" element={<SaleDetail />} />
            <Route path="sales/:id/edit" element={<SaleEdit />} />

            {/* You can decide whether staff_POS sees customers/products; 
                for now I’m leaving them common. */}
            <Route path="customers" element={<CustomerList />} />
            <Route path="customers/new" element={<CustomerEntry />} />
            <Route path="customers/:id" element={<CustomerDetail />} />

            <Route path="products" element={<ProductList />} />
            <Route path="products/new" element={<ProductEntry />} />
            <Route path="products/:id" element={<ProductDetail />} />

            {/* Account pages – safe for both */}
            <Route path="profile" element={<Profile />} />
            <Route path="account/password" element={<ChangePassword />} />

            {/* Payment callbacks – safe for both */}
            <Route path="payments/success" element={<PaymentSuccess />} />
            <Route path="payments/cancel" element={<PaymentCancel />} />
            
            {/* --- ADMIN-ONLY BLOCK (gated by is_company_admin) --- */}
            <Route element={<RequireCompanyAdmin />}>
              {/* Back-office / accounting stuff */}
              <Route path="vendors" element={<VendorList />} />
              <Route path="vendors/new" element={<VendorEntry />} />
              <Route path="vendors/:id" element={<VendorDetail />} />

              <Route path="purchases" element={<PurchasesList />} />
              <Route path="purchases/new" element={<PurchaseEntry />} />
              <Route path="purchases/:id" element={<PurchaseDetail />} />
              <Route path="purchases/:id/edit" element={<PurchaseEdit />} />

              <Route path="receivables" element={<ReceivablesList />} />
              <Route path="payables" element={<PayablesList />} />
              <Route path="payables/credit-card" element={<CreditCardPayableDetail />} />
              <Route path="payables/:bill_id" element={<PurchaseDetail />} />

              <Route path="books" element={<Books />} />
              <Route path="general-journal" element={<GeneralJournal />} />
              <Route path="general-ledger" element={<GeneralLedger />} />
              <Route path="reports" element={<Reports />} />
              <Route path="reports/balance-sheet" element={<BalanceSheet />} />
              <Route path="reports/income-statement" element={<IncomeStatement />} />
              <Route path="reports/cash-analysis" element={<CashAnalysis />} />
              <Route path="reports/trial-balance" element={<TrialBalance />} />

              {/* Settings – company-wide admin only */}
              <Route path="settings" element={<Settings />} />
              <Route path="settings/company-profile" element={<CompanyProfile />} />
              <Route path="settings/payment-methods" element={<PaymentMethodList />} />
              <Route path="settings/payment-methods/new" element={<AddPaymentMethod />} />
              <Route path="settings/payment-methods/:id/edit" element={<EditPaymentMethod />} />
              <Route path="settings/accounts" element={<BankAccountList />} />
              <Route path="settings/accounts/new" element={<AddBankAccount />} />
              <Route path="settings/accounts/:id/edit" element={<EditBankAccount />} />


              {/* Commerce */}
              <Route path="commerce" element={<CommerceCenter />} />
              <Route path="commerce/shopify" element={<ShopifySettings />} />
              <Route path="commerce/shopify/product-mapping" element={<ShopifyProductMapping />} />
              <Route path="commerce/shopify/orders" element={<ShopifyImportedOrders />} />
              <Route path="commerce/shopify/sync-issues" element={<ShopifySyncIssues />} />
              <Route path="commerce/shopify/diagnostics" element={<ShopifyWebhookEvents />} />

              {/* <Route path="settings/expense-categories" element={<ExpenseCategories />} />  */}
              <Route path="settings/editexpensecategories" element={<EditExpenseCategories />} />
              <Route path="settings/year-end" element={<YearEndClosePage />} />
              <Route path="settings/privacy-controls" element={<PrivacyControls />} />
              <Route path="settings/invoice-email" element={<InvoiceEmailSettings />} />
              <Route path="settings/staff-invite" element={<StaffInvite />} />

              {/* Spending hub lists */}
              <Route path="spending" element={<SpendingLayout />}>
                <Route index element={<Navigate to="expenses" replace />} />
                <Route path="expenses" element={<ExpensesList />} />
                <Route path="deposits" element={<SpendingDepositsList />} />
                <Route path="fixed-assets" element={<SpendingFixedAssetsList />} />
              </Route>

              {/* ✅ Legacy aliases (so /prepaids works even if list lives under /spending/prepaids) */}
              <Route path="expenses" element={<Navigate to="../spending/expenses" replace />} />
              <Route path="prepaids" element={<Navigate to="../spending/prepaids" replace />} />
              <Route path="deposits" element={<Navigate to="../spending/deposits" replace />} />
              <Route path="fixed-assets" element={<Navigate to="../spending/fixed-assets" replace />} />

              {/* Expenses CRUD */}
              <Route path="expenses/new" element={<ExpenseEntry />} />
              <Route path="expenses/:id" element={<ExpenseDetail />} />

              {/* ✅ Prepaids CRUD (THIS is what stops the /login redirect) */}
              <Route path="prepaids/new" element={<NewPrepaid />} />
              <Route path="prepaids/:prepaid_id" element={<PrepaidDetailPage />} />

              {/* Deposits */}
              <Route path="deposits/new" element={<NewDeposit />} />
              <Route path="deposits/:deposit_id" element={<DepositDetailPage />} />
              
              {/* Fixed-Assets */}
              <Route path="fixed-assets/new" element={<NewFixedAsset />} />
              <Route path="fixed-assets/:asset_id" element={<FixedAssetDetail />} />
              <Route path="fixed-assets/:asset_id/edit" element={<FixedAssetEdit />} />
              <Route path="asset-purchases/new" element={<NewFixedAsset />} />
              <Route path="asset-purchases/:asset_id" element={<FixedAssetDetail />} />
              <Route path="asset-purchases/:asset_id/edit" element={<FixedAssetEdit />} />

              {/* Scanner V2 shell — capture/OCR suggestions only, no accounting posting */}
              <Route path="scanner" element={<Scanner />} />

              {/* Recognition Center (single definition only — no duplicate path="recognition") */}
              <Route path="recognition" element={<RecognitionCenter />}>
                <Route index element={<Navigate to="expenses" replace />} />
                <Route path="expenses" element={<ExpensesList />} />
                <Route path="prepaids" element={<SpendingPrepaidsList />} />
                <Route path="deposits" element={<SpendingDepositsList />} />
                <Route path="fixed-assets" element={<SpendingFixedAssetsList />} />
              </Route>

              <Route path="journal-entries/adjusting" element={<ProJournalEntry />} />

              <Route path="owner-funds" element={<OwnerFundsList />} />
              <Route path="owner-funds/new" element={<OwnerFundsEntry />} />


              {/* Admin (company-level) */}
              <Route path="admin/users" element={<AdminUserList />} />
            </Route>
          </Route>
        </Route>

        {/* Catch-all → login */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
